// Was ist bei diesem Anrufversuch passiert?
//
// Destiny, 17.08.2026: „Telefonversuche werden nicht notiert, ob man jemanden
// erreicht oder die Mailbox erwischt hat. Dasselbe gilt für die Akte."
//
// Bewusst getrennt vom Kunden-Status („Vereinbart", „Nicht erreicht 2x"): der
// Status sagt, wo die Person im Prozess steht, das Ergebnis sagt, was am
// Telefon passiert ist. „Mailbox erwischt" ist kein Status — aber genau die
// Angabe, die dem Team gefehlt hat, um Kontext zu lesen.

export type AnrufErgebnis =
  | 'erreicht'
  | 'mailbox'
  | 'nachricht'
  | 'aufgelegt'
  | 'niemand'
  | 'besetzt'
  | 'falsche_nummer'
  | 'nicht_waehlbar'
  | 'fehlgeschlagen'
  | 'unbekannt';

interface ErgebnisInfo {
  label: string;
  /** Kurzform für enge Stellen (Listen, Badges). */
  kurz: string;
  /** Zählt als Gespräch? Nur „erreicht". */
  erreicht: boolean;
  farbe: string;
  hintergrund: string;
}

// Farben kommen aus den Ton-Variablen und nicht als feste Werte, damit das
// Ergebnis auch im dunklen Modus lesbar bleibt: die satten Tagesfarben
// (dunkelgrün, dunkelviolett) verschwanden auf den fast schwarzen Flächen —
// in den Wiedervorlagen war „Mailbox" dort praktisch nicht zu lesen. Die Töne
// hellen im dunklen Modus auf, die Fläche wird aus dem Ton über der aktuellen
// Oberfläche gemischt.
function ton(name: string): { farbe: string; hintergrund: string } {
  return {
    farbe: `var(--ton-${name})`,
    hintergrund: `color-mix(in srgb, var(--ton-${name}) 12%, var(--surface))`,
  };
}

export const ERGEBNIS_INFO: Record<AnrufErgebnis, ErgebnisInfo> = {
  erreicht: {
    label: 'Erreicht — gesprochen', kurz: 'erreicht', erreicht: true,
    ...ton('gruen'),
  },
  mailbox: {
    label: 'Mailbox / Anrufbeantworter', kurz: 'Mailbox', erreicht: false,
    ...ton('lila'),
  },
  // Nur bei eingehenden Anrufen: der Anrufer hat uns etwas hinterlassen bzw.
  // vorher aufgelegt. Wird von der Telefonanlage gesetzt, nicht von Hand —
  // deshalb stehen beide nicht in der Auswahl der Nachbereitung.
  nachricht: {
    label: 'Nachricht auf unserer Mailbox', kurz: 'Nachricht', erreicht: false,
    ...ton('lila'),
  },
  aufgelegt: {
    label: 'Aufgelegt, ohne Nachricht', kurz: 'aufgelegt', erreicht: false,
    ...ton('orange'),
  },
  niemand: {
    label: 'Niemand abgenommen', kurz: 'niemand dran', erreicht: false,
    ...ton('gelb'),
  },
  besetzt: {
    label: 'Besetzt', kurz: 'besetzt', erreicht: false,
    ...ton('gelb'),
  },
  falsche_nummer: {
    label: 'Falsche Nummer', kurz: 'falsche Nummer', erreicht: false,
    ...ton('rot'),
  },
  nicht_waehlbar: {
    label: 'Nummer nicht wählbar', kurz: 'nicht wählbar', erreicht: false,
    ...ton('rot'),
  },
  fehlgeschlagen: {
    label: 'Technisch fehlgeschlagen', kurz: 'fehlgeschlagen', erreicht: false,
    ...ton('rot'),
  },
  unbekannt: {
    label: 'Ergebnis nicht erfasst', kurz: 'ohne Ergebnis', erreicht: false,
    ...ton('grau'),
  },
};

// Reihenfolge der Auswahl in der Nachbereitung — die häufigsten zuerst.
export const ERGEBNIS_AUSWAHL: AnrufErgebnis[] = [
  'erreicht', 'mailbox', 'niemand', 'besetzt', 'falsche_nummer',
];

export function istErgebnis(wert: unknown): wert is AnrufErgebnis {
  return typeof wert === 'string' && wert in ERGEBNIS_INFO;
}

/**
 * Beschriftung für ein gespeichertes Ergebnis. Alte Anrufe (aus dem Altsystem
 * oder von vor dieser Erfassung) haben keins — dann entscheidet „erreicht"
 * bzw. der technische Status der Telefonanlage, damit in der Akte nie ein
 * leeres Feld steht.
 */
export function ergebnisText(
  ergebnis: string | null | undefined,
  answered: boolean | null | undefined,
  anlageStatus?: string | null,
): { text: string; farbe: string; hintergrund: string; erreicht: boolean } {
  if (istErgebnis(ergebnis) && ergebnis !== 'unbekannt') {
    const info = ERGEBNIS_INFO[ergebnis];
    return { text: info.kurz, farbe: info.farbe, hintergrund: info.hintergrund, erreicht: info.erreicht };
  }
  if (answered) {
    const info = ERGEBNIS_INFO.erreicht;
    return { text: info.kurz, farbe: info.farbe, hintergrund: info.hintergrund, erreicht: true };
  }
  const info = ERGEBNIS_INFO.unbekannt;
  return {
    text: anlageStatus && anlageStatus !== 'unbekannt' ? anlageStatus : 'nicht erreicht',
    farbe: info.farbe, hintergrund: info.hintergrund, erreicht: false,
  };
}
